import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ShoppingBag, X, Trash2 } from 'lucide-react';
import { useQuoteStore } from '../../stores/quote';
import { formatPrice } from '../../lib/shop-products';

/**
 * Header cart badge + slide-in drawer. Mount once in app chrome — Zustand
 * subscription keeps the count live across every route.
 */
export function QuoteCart() {
  const [open, setOpen] = useState(false);
  const items = useQuoteStore((s) => s.items);
  const remove = useQuoteStore((s) => s.remove);
  const setQty = useQuoteStore((s) => s.setQty);
  const total = items.reduce((n, i) => n + i.price * i.qty, 0);
  const count = items.reduce((n, i) => n + i.qty, 0);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="relative flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-sm font-medium text-neutral-200 transition hover:bg-white/[0.08]"
          title="Your quote list"
        >
          <ShoppingBag className="h-4 w-4" />
          <span className="hidden sm:inline">Quote</span>
          {count > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-600 px-1.5 text-[11px] font-semibold text-white">
              {count}
            </span>
          )}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-md flex-col border-l border-white/10 bg-neutral-950 shadow-2xl data-[state=open]:animate-in data-[state=open]:slide-in-from-right">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <Dialog.Title className="text-sm font-semibold">Your quote list</Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className="rounded-md p-1 text-neutral-400 hover:bg-white/5 hover:text-neutral-100">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {items.length === 0 ? (
              <div className="mt-8 text-center text-sm text-neutral-500">
                Your quote list is empty.
                <div className="mt-4">
                  <a href="#shop" onClick={() => setOpen(false)} className="btn-secondary inline-flex">Browse the shop</a>
                </div>
              </div>
            ) : (
              <ul className="space-y-3">
                {items.map((item) => (
                  <li key={`${item.slug}:${item.optionId}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold">{item.name}</div>
                        <div className="text-xs text-neutral-400">{item.optionLabel}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(item.slug, item.optionId)}
                        className="rounded-md p-1 text-neutral-500 hover:bg-white/5 hover:text-red-400"
                        title="Remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setQty(item.slug, item.optionId, item.qty - 1)}
                          className="h-6 w-6 rounded border border-white/10 text-xs hover:bg-white/5"
                          aria-label="Decrease"
                        >
                          −
                        </button>
                        <span className="w-6 text-center text-xs">{item.qty}</span>
                        <button
                          type="button"
                          onClick={() => setQty(item.slug, item.optionId, item.qty + 1)}
                          className="h-6 w-6 rounded border border-white/10 text-xs hover:bg-white/5"
                          aria-label="Increase"
                        >
                          +
                        </button>
                      </div>
                      <div className="text-sm font-semibold text-brand-300">
                        {formatPrice(item.price * item.qty)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t border-white/10 px-5 py-4">
              <div className="mb-3 flex items-center justify-between text-sm">
                <span className="text-neutral-400">Estimated total</span>
                <span className="font-semibold">{formatPrice(total)}</span>
              </div>
              <p className="mb-3 text-[11px] leading-relaxed text-neutral-500">
                Indicative pricing only. Final quote sent within 24h.
              </p>
              <a
                href="#quote"
                onClick={() => setOpen(false)}
                className="btn-primary w-full"
              >
                Request your quote →
              </a>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
